import React from 'react';
import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  type IssueSummary,
  type IssueDetailResponse,
  type PaginatedIssuesResponse,
  type PaginatedIssueCommentsResponse,
  type IssueComment,
  type IssueStatus,
  type ConsoleLogEntry,
  type NetworkRequestEntry,
} from '@chatsdet/types/issues';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';
import type { WrongOrgInfo } from '../ui/components/OrgSwitchPrompt';

export type IssueSortField = 'severity' | 'status' | 'category' | 'title' | 'path' | 'lastSeenAt';
export type SortOrder = 'asc' | 'desc';

type UseIssuesV2Params = {
  page?: number;
  pageSize?: number;
  severity?: IssueSummary['severity'] | 'all';
  propertyId?: string | 'all';
  statuses?: IssueStatus[];
  search?: string;
  lastSeenStart?: string;
  lastSeenEnd?: string;
  categoryIds?: string[];
  credentialIds?: string[];
  tagIds?: string[];
  includeArchived?: boolean;
  sortBy?: IssueSortField;
  sortOrder?: SortOrder;
};

type IssuesV2QueryOptions = Omit<UseQueryOptions<PaginatedIssuesResponse>, 'queryKey' | 'queryFn'>;

export function useIssuesV2Query(params: UseIssuesV2Params, options?: IssuesV2QueryOptions) {
  const page = Math.max(params.page ?? 1, 1);
  const pageSize = Math.max(params.pageSize ?? 25, 1);
  const severityParam =
    params.severity && params.severity !== 'all'
      ? (params.severity as IssueSummary['severity'])
      : undefined;
  const propertyParam =
    params.propertyId && params.propertyId !== 'all' ? params.propertyId : undefined;
  const searchParam = params.search?.trim() ?? '';
  const lastSeenStart = params.lastSeenStart;
  const lastSeenEnd = params.lastSeenEnd;
  const categoryIds = Array.isArray(params.categoryIds)
    ? params.categoryIds.filter((id) => typeof id === 'string' && id.trim().length > 0)
    : [];
  const normalizedCategoryIds =
    categoryIds.length > 0
      ? Array.from(new Set(categoryIds.map((id) => id.trim()))).filter((id) => id.length > 0)
      : [];
  const credentialIds = Array.isArray(params.credentialIds)
    ? params.credentialIds.filter((id) => typeof id === 'string' && id.trim().length > 0)
    : [];
  const normalizedCredentialIds =
    credentialIds.length > 0
      ? Array.from(new Set(credentialIds.map((id) => id.trim()))).filter((id) => id.length > 0)
      : [];
  const tagIds = Array.isArray(params.tagIds)
    ? params.tagIds.filter((id) => typeof id === 'string' && id.trim().length > 0)
    : [];
  const normalizedTagIds =
    tagIds.length > 0
      ? Array.from(new Set(tagIds.map((id) => id.trim()))).filter((id) => id.length > 0)
      : [];
  const includeArchived = params.includeArchived === true;
  // 'all' sentinel means show all statuses (no filter)
  const statusesParam = params.statuses ?? ['open'];
  const statuses = statusesParam.includes('all' as IssueStatus) ? [] : statusesParam;
  const sortBy = params.sortBy;
  const sortOrder = params.sortOrder;

  return useQuery<PaginatedIssuesResponse>({
    queryKey: [
      'issues-v2',
      {
        page,
        pageSize,
        severity: severityParam ?? 'all',
        property: propertyParam ?? 'all',
        statuses,
        search: searchParam,
        lastSeenStart: lastSeenStart ?? '',
        lastSeenEnd: lastSeenEnd ?? '',
        categoryIds: normalizedCategoryIds,
        credentialIds: normalizedCredentialIds,
        tagIds: normalizedTagIds,
        includeArchived,
        sortBy: sortBy ?? '',
        sortOrder: sortOrder ?? '',
      },
    ],
    queryFn: async () => {
      const search = new URLSearchParams();
      search.set('page', String(page));
      search.set('pageSize', String(pageSize));
      if (severityParam) search.set('severity', severityParam);
      if (propertyParam) search.set('propertyId', propertyParam);
      if (statuses.length > 0) search.set('statuses', statuses.join(','));
      if (searchParam) search.set('search', searchParam);
      if (lastSeenStart) search.set('lastSeenStart', lastSeenStart);
      if (lastSeenEnd) search.set('lastSeenEnd', lastSeenEnd);
      if (normalizedCategoryIds.length > 0) {
        search.set('categoryIds', normalizedCategoryIds.join(','));
      }
      if (normalizedCredentialIds.length > 0) {
        search.set('credentialIds', normalizedCredentialIds.join(','));
      }
      if (normalizedTagIds.length > 0) {
        search.set('tagIds', normalizedTagIds.join(','));
      }
      if (includeArchived) {
        search.set('includeArchived', 'true');
      }
      if (sortBy) {
        search.set('sortBy', sortBy);
      }
      if (sortOrder) {
        search.set('sortOrder', sortOrder);
      }

      const response = await fetch(`${API_URL}/v2/issues?${search.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch issues');
      }

      const body = await response.json();
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to fetch issues');
      }

      return {
        data: Array.isArray(body.data) ? (body.data as IssueSummary[]) : [],
        pagination: body.pagination,
      } satisfies PaginatedIssuesResponse;
    },
    ...options,
  });
}

type UseIssueDetailV2Params = {
  page?: number;
  pageSize?: number;
};

type IssueDetailV2Options = Omit<UseQueryOptions<IssueDetailResponse>, 'queryKey' | 'queryFn'>;

export function useIssueDetailV2(
  issueId: string | null,
  params?: UseIssueDetailV2Params,
  options?: IssueDetailV2Options
) {
  const page = Math.max(params?.page ?? 1, 1);
  const pageSize = Math.max(params?.pageSize ?? 10, 1);
  const [wrongOrgInfo, setWrongOrgInfo] = React.useState<WrongOrgInfo | null>(null);

  const query = useQuery<IssueDetailResponse>({
    queryKey: ['issue-detail-v2', issueId, page, pageSize],
    enabled: Boolean(issueId),
    queryFn: async () => {
      if (!issueId) {
        throw new Error('Issue ID is required');
      }

      const search = new URLSearchParams();
      search.set('occurrencePage', String(page));
      search.set('occurrencePageSize', String(pageSize));

      const response = await fetch(`${API_URL}/v2/issues/${issueId}?${search.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'ISSUE_WRONG_ORG' && errorData.targetOrgId && errorData.targetOrgName) {
          setWrongOrgInfo({
            orgId: errorData.targetOrgId,
            orgName: errorData.targetOrgName,
          });
        }
        throw await toApiError(response, 'Failed to fetch issue detail');
      }

      const body = await response.json();
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to fetch issue detail');
      }

      // Clear wrong org info on successful fetch
      setWrongOrgInfo(null);
      return body.data as IssueDetailResponse;
    },
    ...options,
  });

  const clearWrongOrgInfo = React.useCallback(() => setWrongOrgInfo(null), []);

  return { ...query, wrongOrgInfo, clearWrongOrgInfo };
}

type IssueCommentsV2Options = Omit<
  UseQueryOptions<PaginatedIssueCommentsResponse>,
  'queryKey' | 'queryFn'
>;

export function useIssueCommentsV2(
  issueId: string | null,
  params: UseIssueDetailV2Params,
  options?: IssueCommentsV2Options
) {
  const page = Math.max(params.page ?? 1, 1);
  const pageSize = Math.max(params.pageSize ?? 25, 1);

  return useQuery<PaginatedIssueCommentsResponse>({
    queryKey: ['issue-comments-v2', issueId, page, pageSize],
    enabled: Boolean(issueId),
    queryFn: async () => {
      if (!issueId) {
        throw new Error('Issue ID is required');
      }

      const search = new URLSearchParams();
      search.set('page', String(page));
      search.set('pageSize', String(pageSize));

      const response = await fetch(
        `${API_URL}/v2/issues/${issueId}/comments?${search.toString()}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch issue comments');
      }

      const body = await response.json();
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to fetch issue comments');
      }

      return {
        data: Array.isArray(body.data) ? (body.data as IssueComment[]) : [],
        pagination: body.pagination,
      } satisfies PaginatedIssueCommentsResponse;
    },
    ...options,
  });
}

type CreateIssueCommentResponse = {
  ok: boolean;
  comment: IssueComment;
};

export function useCreateIssueCommentMutation(issueId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: string) => {
      const response = await fetch(`${API_URL}/v2/issues/${issueId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ body }),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to create comment');
      }

      const parsed = (await response.json()) as CreateIssueCommentResponse;
      if (!parsed?.ok || !parsed.comment) {
        throw new Error('Failed to create comment');
      }

      return parsed.comment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issue-comments-v2', issueId] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issue-detail-v2', issueId] }).catch(() => {});
    },
  });
}

type AssignIssueResponse = {
  ok: boolean;
  assignedToId: string | null;
};

export function useAssignIssueMutation(issueId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assignedToId: string | null) => {
      const response = await fetch(`${API_URL}/issues/${issueId}/assign`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ assignedToId }),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to assign issue');
      }

      const body = await response.json();
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to assign issue');
      }

      return body as AssignIssueResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues-v2'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issue-detail-v2', issueId] }).catch(() => {});
    },
  });
}

type ArchiveIssueResponse = {
  ok: boolean;
  archivedCount: number;
};

export function useArchiveIssueMutation(issueId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/issues/${issueId}/archive`, {
        method: 'PATCH',
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to archive issue');
      }

      const body = await response.json();
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to archive issue');
      }

      return body as ArchiveIssueResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['v2-issues'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issues-v2'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issue-detail-v2', issueId] }).catch(() => {});
    },
  });
}

type UpdateIssueStatusResponse = {
  ok: boolean;
  status: IssueStatus;
  statusUpdatedAt: string;
  closedAt: string | null;
};

export function useUpdateIssueStatusMutation(issueId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (status: IssueStatus) => {
      const response = await fetch(`${API_URL}/issues/${issueId}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to update issue status');
      }

      const body = await response.json();
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to update issue status');
      }

      return body as UpdateIssueStatusResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['v2-issues'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issues-v2'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issue-detail-v2', issueId] }).catch(() => {});
    },
  });
}

type UpdateIssueSeverityResponse = {
  ok: boolean;
  severity: IssueSummary['severity'];
  updatedAt: string;
};

export function useUpdateIssueSeverityMutation(issueId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (severity: IssueSummary['severity']) => {
      const response = await fetch(`${API_URL}/issues/${issueId}/severity`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ severity }),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to update issue severity');
      }

      const body = await response.json();
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to update issue severity');
      }

      return body as UpdateIssueSeverityResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['v2-issues'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issues-v2'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issue-detail-v2', issueId] }).catch(() => {});
    },
  });
}

type UpdateIssueCategoryResponse = {
  ok: boolean;
  categoryId: string;
  categoryKey: string;
  category: IssueSummary['category'];
  updatedAt: string;
};

export function useUpdateIssueCategoryMutation(issueId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryId: string) => {
      const response = await fetch(`${API_URL}/issues/${issueId}/category`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ categoryId }),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to update issue category');
      }

      const body = await response.json();
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to update issue category');
      }

      return body as UpdateIssueCategoryResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['v2-issues'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issues-v2'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issue-detail-v2', issueId] }).catch(() => {});
    },
  });
}

// Lazy-loading hooks for occurrence artifacts

type ConsoleLogsOptions = Omit<UseQueryOptions<ConsoleLogEntry[]>, 'queryKey' | 'queryFn'>;

/**
 * Fetches console logs for a specific issue occurrence.
 * Data is loaded on-demand from S3 storage.
 */
export function useIssueConsoleLogs(
  issueId: string | null,
  occurrenceId: string | null,
  options?: ConsoleLogsOptions
) {
  return useQuery<ConsoleLogEntry[]>({
    queryKey: ['issue-console-logs', issueId, occurrenceId],
    enabled: Boolean(issueId) && Boolean(occurrenceId),
    queryFn: async () => {
      if (!issueId || !occurrenceId) {
        throw new Error('Issue ID and Occurrence ID are required');
      }

      const response = await fetch(
        `${API_URL}/v2/issues/${issueId}/occurrences/${occurrenceId}/console-logs`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch console logs');
      }

      const body = (await response.json()) as {
        ok: boolean;
        data?: ConsoleLogEntry[];
        error?: string;
      };
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to fetch console logs');
      }

      return body.data ?? [];
    },
    ...options,
  });
}

type NetworkRequestsOptions = Omit<UseQueryOptions<NetworkRequestEntry[]>, 'queryKey' | 'queryFn'>;

/**
 * Fetches network requests for a specific issue occurrence.
 * Data is loaded on-demand from S3 storage.
 */
export function useIssueNetworkRequests(
  issueId: string | null,
  occurrenceId: string | null,
  options?: NetworkRequestsOptions
) {
  return useQuery<NetworkRequestEntry[]>({
    queryKey: ['issue-network-requests', issueId, occurrenceId],
    enabled: Boolean(issueId) && Boolean(occurrenceId),
    queryFn: async () => {
      if (!issueId || !occurrenceId) {
        throw new Error('Issue ID and Occurrence ID are required');
      }

      const response = await fetch(
        `${API_URL}/v2/issues/${issueId}/occurrences/${occurrenceId}/network-requests`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch network requests');
      }

      const body = (await response.json()) as {
        ok: boolean;
        data?: NetworkRequestEntry[];
        error?: string;
      };
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to fetch network requests');
      }

      return body.data ?? [];
    },
    ...options,
  });
}

export function useRetryDiagnosticsMutation(issueId: string, occurrenceId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!occurrenceId) {
        throw new Error('Occurrence ID is required');
      }

      const response = await fetch(
        `${API_URL}/v2/issues/${issueId}/occurrences/${occurrenceId}/diagnostics/retry`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw await toApiError(response, 'Failed to retry diagnostics');
      }

      const body = await response.json();
      if (!body?.ok) {
        throw new Error(body?.error ?? 'Failed to retry diagnostics');
      }

      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issue-detail-v2', issueId] }).catch(() => {});
    },
  });
}

